// components/MultiTextInput.tsx
import * as React from "react";
import { Autocomplete, TextField } from "@mui/material";

type MultiTextInputProps = {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  errorText?: string;
  normalize?: (s: string) => string; // e.g. strip leading "@"
};

export default function MultiTextInput({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  errorText,
  normalize,
}: MultiTextInputProps) {
  const [inputValue, setInputValue] = React.useState("");

  const clean = (s: string) => {
    const v = (normalize ? normalize(s) : s).trim();
    return v;
  };

  const addToken = (raw: string) => {
    const v = clean(raw);
    if (!v) return;
    // avoid duplicates (case-insensitive)
    const lowerSet = new Set(value.map((x) => x.toLowerCase()));
    if (!lowerSet.has(v.toLowerCase())) {
      onChange([...value, v]);
    }
    setInputValue("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!inputValue) return;
    if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
      e.preventDefault();
      addToken(inputValue);
    }
  };

  const handleBlur = () => {
    if (inputValue) addToken(inputValue);
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      disableClearable
      options={[]}
      value={value}
      onChange={(_e, newValue) => {
        const cleaned = newValue
          .map(clean)
          .filter(Boolean);

        // de-dupe case-insensitively
        const seen = new Set<string>();
        const deduped = cleaned.filter((v) => {
          const key = v.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        onChange(deduped);
      }}
      inputValue={inputValue}
      onInputChange={(_e, newInput) => setInputValue(newInput)}
      onKeyDown={handleKeyDown}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          disabled={disabled}
          error={Boolean(errorText)}
          helperText={errorText || "Press Enter to add"}
          onBlur={handleBlur}
        />
      )}
    />
  );
}
